import * as THREE from 'three'
import { computed } from 'vue'
import type { Ref } from 'vue'
import { acceleratedRaycast } from 'three-mesh-bvh'
import { Nido } from '~/service/experience/world/nido/Nido'
import { Point } from '~/service/experience/world/nido/Point'

const DISTANCE = 12

let instance: THREE.Raycaster
let editMode: boolean
let points: Ref<
  { element: HTMLElement; position: THREE.Vector3; active: boolean }[]
>

let scales: Ref<
  { element: HTMLElement; position: THREE.Vector3; value: number }[]
>

let pointsRef: Ref<HTMLElement[]>
let scalesRef: Ref<HTMLElement[]>
let pointsVal: Ref<Point[]>
let camera: ReturnType<typeof useCamera>
let sizes: ReturnType<typeof useSizes>
let objects: Nido[]
let world: ReturnType<typeof useWorld>

export function useRaycaster() {
  function init() {
    if (!world) {
      world = useWorld()
    }
    if (!camera) {
      camera = useCamera()
    }

    sizes = useSizes()
    objects = world.nidoObjects
    instance = new THREE.Raycaster()
    editMode = true
    THREE.Mesh.prototype.raycast = acceleratedRaycast
    instance.firstHitOnly = true

    points = computed(() => {
      const arr = []
      for (let i = 0; i < pointsVal.value.length; i++) {
        arr.push({
          ...pointsVal.value[i],
          element: pointsRef.value[i],
        })
      }
      return arr
    })

    scales = computed(() => {
      const arr = []
      for (let i = 0; i < scalesRef.value.length; i++) {
        arr.push({
          ...useWorld().textScales.value[i],
          element: scalesRef.value[i],
        })
      }
      return arr
    })
  }

  function update() {
    if (editMode) {
      // Update points
      points.value.forEach((point) => {
        if (!point.active) {
          point.element.classList.remove('visible')
          return
        }

        if (camera.instance.position.distanceTo(point.position) > DISTANCE) {
          point.element.classList.remove('visible')
          return
        }

        const screenPosition = point.position.clone()
        screenPosition.project(camera.instance)

        instance.setFromCamera(screenPosition, camera.instance)

        const intersects = instance.intersectObjects(
          objects.map((object) => object.collider)
        )
        const pointDistance = point.position.distanceTo(
          camera.instance.position
        )

        if (intersects.length === 0) {
          point.element.classList.add('visible')
        } else {
          const intersectionDistance = intersects[0].distance

          if (intersectionDistance < pointDistance) {
            point.element.classList.remove('visible')
          } else {
            point.element.classList.add('visible')
          }
        }

        const translateX = screenPosition.x * sizes.width.value * 0.5
        const translateY = -screenPosition.y * sizes.height.value * 0.5
        const zIndex = Math.floor(10000 - screenPosition.z * 10000)
        // const scale = screenPosition.z

        if (zIndex < 0) {
          point.element.classList.remove('visible')
        }

        point.element.style.zIndex = `${zIndex}`
        point.element.style.transform = `translateX(${translateX}px) translateY(${translateY}px) scale(${
          3 / pointDistance
        })`
      })
    }

    if (useWorld().scalesVisible) {
      scales.value.forEach((point) => {
        if (!point.position) return

        const screenPosition = point.position.clone()
        screenPosition.project(camera.instance)

        instance.setFromCamera(screenPosition, camera.instance)

        const intersects = instance.intersectObjects(
          objects.map((object) => object.collider)
        )

        if (intersects.length === 0) {
          point.element.classList.add('visible')
        } else {
          const intersectionDistance = intersects[0].distance
          const pointDistance = point.position.distanceTo(
            camera.instance.position
          )

          if (intersectionDistance < pointDistance) {
            point.element.classList.remove('visible')
          } else {
            point.element.classList.add('visible')
          }
        }

        const translateX = screenPosition.x * sizes.width.value * 0.5
        const translateY = -screenPosition.y * sizes.height.value * 0.5
        const zIndex = Math.floor(10000 - screenPosition.z * 10000)
        // const scale = screenPosition.z

        if (zIndex < 0) {
          point.element.classList.remove('visible')
        }

        point.element.style.zIndex = `${zIndex}`
        point.element.style.transform = `translateX(${translateX}px) translateY(${translateY}px)`
        // point.element.style.scale = `${(1 - scale) * 10}`
      })
    }
  }

  function setPoints(points: Ref<Point[]>, _pointsRef: Ref<HTMLElement[]>) {
    pointsVal = points
    pointsRef = _pointsRef
  }

  function checkPoints() {
    const arr: Point[] = []
    objects.forEach((object) => {
      Object.entries(object.points).forEach(([, value]) => {
        arr.push(value)
      })
    })
    pointsVal.value = arr
  }

  function setNumbers(_scalesRef: Ref<HTMLElement[]>) {
    scalesRef = _scalesRef
  }

  function destroy() {
    instance = undefined
    editMode = undefined
    points = undefined
    scales = undefined
    pointsRef = undefined
    scalesRef = undefined
    pointsVal = undefined
    camera = undefined
    sizes = undefined
    objects = undefined
    world = undefined
  }

  return {
    init,
    update,
    setPoints,
    checkPoints,
    setNumbers,
    destroy,
    points,
    pointsRef,
    pointsVal,
  }
}
